/**
 * BranchSettingsPage
 * Full settings control for org admins managing a branch.
 * Mirrors SettingsPage.js exactly — same tabs, same fields, same controls.
 * Uses branchApi (scoped to the branch via ?facility=<id>) instead of facilityAdmin.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { makeBranchApi } from "../../api/branchApi";
import {
  PageHeader, Btn, FormField,
  inputStyle, selectStyle, LoadingSpinner,
} from "../../components/ui";
import toast from "react-hot-toast";

const TABS = ["Queue Behaviour", "Notifications", "Kiosk", "Display Board"];

export default function BranchSettingsPage() {
  const { branchId }        = useParams();
  const api                 = makeBranchApi(branchId);

  const [tab,      setTab]     = useState(0);
  const [settings, setSettings]= useState(null);
  const [loading,  setLoading] = useState(true);
  const [saving,   setSaving]  = useState(false);
  const [dirty,    setDirty]   = useState(false);

  useEffect(() => {
    api.getSettings()
      .then(r => setSettings(r.data))
      .catch(() => toast.error("Failed to load settings."))
      .finally(() => setLoading(false));
  }, [branchId]);

  const set = (k, v) => {
    setSettings(p => ({ ...p, [k]: v }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings(settings);
      toast.success("Settings saved.");
      setDirty(false);
    } catch { toast.error("Failed to save settings."); }
    finally  { setSaving(false); }
  };

  if (loading) return <div style={{ padding: "32px" }}><LoadingSpinner /></div>;
  if (!settings) return <div style={{ padding: "32px", color: "#EF4444" }}>Failed to load settings.</div>;

  return (
    <div style={styles.root}>
      <PageHeader
        title="Settings"
        subtitle="Configure how this branch's queue system behaves."
        action={
          <Btn onClick={handleSave} disabled={saving || !dirty}>
            {saving ? "Saving…" : dirty ? "Save Changes" : "Saved ✓"}
          </Btn>
        }
      />

      <div style={styles.tabs}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            style={{ ...styles.tab, ...(tab === i ? styles.tabActive : {}) }}>
            {t}
          </button>
        ))}
      </div>

      <div style={styles.panel}>
        {tab === 0 && <QueueTab s={settings} set={set} />}
        {tab === 1 && <NotifTab s={settings} set={set} />}
        {tab === 2 && <KioskTab s={settings} set={set} />}
        {tab === 3 && <DisplayTab s={settings} set={set} />}
      </div>

      {dirty && (
        <div style={styles.saveBar}>
          <span style={{ fontSize: "13px", color: "#EAB308" }}>You have unsaved changes.</span>
          <Btn onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</Btn>
        </div>
      )}
    </div>
  );
}

// ── Tab: Queue Behaviour ──────────────────────────────────────────────────────
function QueueTab({ s, set }) {
  return (
    <div style={T.grid}>
      <Section title="Ticket Issuance">
        <FormField label="Issuance Mode" hint="Who can issue queue tickets">
          <select style={selectStyle} value={s.issuance_mode} onChange={e => set("issuance_mode", e.target.value)}>
            <option value="kiosk_only">Kiosk Only</option>
            <option value="kiosk_and_staff">Kiosk + Staff</option>
            <option value="staff_only">Staff Only</option>
          </select>
        </FormField>
        <FormField label="Daily Queue Reset Time" hint="Counters reset to 0 at this time each day">
          <input style={inputStyle} type="time" value={s.reset_time || "00:00"} onChange={e => set("reset_time", e.target.value)} />
        </FormField>
      </Section>

      <Section title="Priority">
        <FormField label="Priority Assignment Mode">
          <select style={selectStyle} value={s.priority_mode} onChange={e => set("priority_mode", e.target.value)}>
            <option value="staff_only">Staff Only — staff manually mark priority</option>
            <option value="self_declare">Kiosk + Staff — patient can self-declare</option>
            <option value="automatic">Automatic — by age threshold</option>
          </select>
        </FormField>
        {s.priority_mode === "automatic" && (
          <FormField label="Priority Age Threshold" hint="Patients this age or older get auto-priority">
            <input style={inputStyle} type="number" value={s.priority_age_threshold} onChange={e => set("priority_age_threshold", parseInt(e.target.value))} min={1} max={120} />
          </FormField>
        )}
      </Section>
    </div>
  );
}

// ── Tab: Notifications ────────────────────────────────────────────────────────
function NotifTab({ s, set }) {
  return (
    <div style={T.grid}>
      <Section title="Wigal API Credentials">
        <FormField label="Wigal API Key">
          <input style={inputStyle} type="password" value={s.wigal_api_key || ""} onChange={e => set("wigal_api_key", e.target.value)} placeholder="Your Wigal API key" />
        </FormField>
        <FormField label="Sender ID">
          <input style={inputStyle} value={s.wigal_sender_id || ""} onChange={e => set("wigal_sender_id", e.target.value)} placeholder="EliyaQ" />
        </FormField>
      </Section>

      <Section title="Channels">
        <Toggle label="SMS Notifications"      checked={s.sms_enabled}      onChange={v => set("sms_enabled", v)} />
        <Toggle label="WhatsApp Notifications" checked={s.whatsapp_enabled} onChange={v => set("whatsapp_enabled", v)} />
      </Section>

      <Section title="Notification Events">
        <Toggle label="On ticket issued"            checked={s.notify_on_issue}    onChange={v => set("notify_on_issue", v)} />
        <Toggle label="When called to counter"      checked={s.notify_on_called}   onChange={v => set("notify_on_called", v)} />
        <Toggle label="On transfer to next station" checked={s.notify_on_transfer} onChange={v => set("notify_on_transfer", v)} />
        <Toggle label="On journey completion"       checked={s.notify_on_complete} onChange={v => set("notify_on_complete", v)} />
        <Toggle label="Alert when 2 people ahead"   checked={s.notify_2_away}      onChange={v => set("notify_2_away", v)} />
      </Section>
    </div>
  );
}

// ── Tab: Kiosk ────────────────────────────────────────────────────────────────
const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English",  native: "English",  locked: true },
  { code: "tw", name: "Twi",      native: "Twi" },
  { code: "ga", name: "Ga",       native: "Ga" },
  { code: "ee", name: "Ewe",      native: "Eʋegbe" },
  { code: "ha", name: "Hausa",    native: "Hausa" },
  { code: "fr", name: "French",   native: "Français" },
];

function KioskTab({ s, set }) {
  const enabledLangs = s.kiosk_enabled_languages || ["en", "tw"];

  const toggleLang = (code) => {
    if (code === "en") return;
    const next = enabledLangs.includes(code)
      ? enabledLangs.filter(c => c !== code)
      : [...enabledLangs, code];
    if (!next.includes("en")) next.unshift("en");
    set("kiosk_enabled_languages", next);
  };

  return (
    <div style={T.grid}>
      <Section title="Languages">
        <div style={{ fontSize: "12px", color: "#64748B", marginBottom: "4px" }}>
          Select which languages appear on the kiosk. English is always available.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          {SUPPORTED_LANGUAGES.map(lang => {
            const active = enabledLangs.includes(lang.code);
            return (
              <button key={lang.code} onClick={() => toggleLang(lang.code)} disabled={lang.locked}
                style={{
                  padding: "8px 16px", borderRadius: "8px", cursor: lang.locked ? "default" : "pointer",
                  fontSize: "13px", fontWeight: "600", fontFamily: "inherit",
                  border: `2px solid ${active ? "#2563EB" : "rgba(51,65,85,0.6)"}`,
                  background: active ? "rgba(37,99,235,0.15)" : "rgba(30,41,59,0.4)",
                  color: active ? "#60A5FA" : "#64748B",
                  opacity: lang.locked ? 0.7 : 1, transition: "all 0.15s",
                }}>
                {lang.native}
                {lang.locked && <span style={{ fontSize: "10px", marginLeft: "6px", color: "#475569" }}>always on</span>}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Accessibility">
        <Toggle
          label="Show accessibility assistance selection step on kiosk"
          checked={s.kiosk_accessibility_enabled !== false}
          onChange={v => set("kiosk_accessibility_enabled", v)}
        />
        <div style={{ fontSize: "12px", color: "#475569" }}>
          When enabled, patients choose their assistance needs after language selection (e.g. hearing, mobility, visual).
        </div>
      </Section>

      <Section title="Welcome Message">
        <FormField label="Welcome (English)">
          <input style={inputStyle} value={s.kiosk_welcome_en || ""} onChange={e => set("kiosk_welcome_en", e.target.value)} placeholder="Welcome. Please get your queue number." />
        </FormField>
        <FormField label="Welcome (Twi)">
          <input style={inputStyle} value={s.kiosk_welcome_tw || ""} onChange={e => set("kiosk_welcome_tw", e.target.value)} placeholder="Akwaaba. Yɛsre wo fa wo queue number." />
        </FormField>
        <FormField label="Footer (English)">
          <input style={inputStyle} value={s.kiosk_footer_en || ""} onChange={e => set("kiosk_footer_en", e.target.value)} placeholder="Optional footer text" />
        </FormField>
        <FormField label="Footer (Twi)">
          <input style={inputStyle} value={s.kiosk_footer_tw || ""} onChange={e => set("kiosk_footer_tw", e.target.value)} placeholder="Twi footer" />
        </FormField>
      </Section>

      <Section title="Kiosk Lock Mode">
        <Toggle label="Kiosk Mode (enable all lock features)"
          checked={s.kiosk_mode} onChange={v => set("kiosk_mode", v)} />
        <Toggle label="Launch in Fullscreen"
          checked={s.kiosk_fullscreen !== false} onChange={v => set("kiosk_fullscreen", v)} />
        <Toggle label="Auto Re-enter Fullscreen if Exited"
          checked={s.kiosk_auto_reenter_fullscreen !== false} onChange={v => set("kiosk_auto_reenter_fullscreen", v)} />
        <Toggle label="Hide Cursor"
          checked={!!s.kiosk_hide_cursor} onChange={v => set("kiosk_hide_cursor", v)} />
        <Toggle label="Prevent Screen Sleep"
          checked={s.kiosk_prevent_sleep !== false} onChange={v => set("kiosk_prevent_sleep", v)} />
      </Section>

      <Section title="Kiosk Behaviour">
        <FormField label="Theme">
          <select style={selectStyle} value={s.kiosk_theme || "dark_blue"} onChange={e => set("kiosk_theme", e.target.value)}>
            <option value="dark_blue">Dark Blue</option>
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
        </FormField>
        <FormField label="Inactivity Timeout (seconds)" hint="Kiosk resets after this many seconds of no interaction">
          <input style={inputStyle} type="number" value={s.kiosk_timeout_seconds || 60} onChange={e => set("kiosk_timeout_seconds", parseInt(e.target.value))} min={10} />
        </FormField>
        <Toggle label="Show queue position to patient"  checked={s.kiosk_show_position}  onChange={v => set("kiosk_show_position", v)} />
        <Toggle label="Show estimated wait time"        checked={s.kiosk_show_wait_time} onChange={v => set("kiosk_show_wait_time", v)} />
        <Toggle label="Audio enabled on kiosk"         checked={s.kiosk_audio_enabled}  onChange={v => set("kiosk_audio_enabled", v)} />
      </Section>
    </div>
  );
}

// ── Tab: Display Board ────────────────────────────────────────────────────────
function DisplayTab({ s, set }) {
  return (
    <div style={T.grid}>
      <Section title="Display Lock Mode">
        <Toggle label="Launch in Fullscreen"
          checked={s.board_fullscreen !== false} onChange={v => set("board_fullscreen", v)} />
        <Toggle label="Auto Re-enter Fullscreen if Exited"
          checked={s.board_auto_reenter_fullscreen !== false} onChange={v => set("board_auto_reenter_fullscreen", v)} />
        <Toggle label="Hide Cursor"
          checked={s.board_hide_cursor !== false} onChange={v => set("board_hide_cursor", v)} />
        <Toggle label="Prevent Screen Sleep"
          checked={s.board_prevent_sleep !== false} onChange={v => set("board_prevent_sleep", v)} />
      </Section>

      <Section title="Display Content">
        <Toggle label="Show staff names on board"   checked={s.board_show_staff_names} onChange={v => set("board_show_staff_names", v)} />
        <Toggle label="Show queue statistics"       checked={s.board_show_stats}       onChange={v => set("board_show_stats", v)} />
        <Toggle label="Show estimated wait time"    checked={s.board_show_wait_time}   onChange={v => set("board_show_wait_time", v)} />
        <Toggle label="Show 'Up Next' list"         checked={s.board_show_up_next}     onChange={v => set("board_show_up_next", v)} />
      </Section>

      <Section title="Layout & Theme">
        <FormField label="Theme">
          <select style={selectStyle} value={s.board_theme || "dark"} onChange={e => set("board_theme", e.target.value)}>
            <option value="dark">Dark</option>
            <option value="dark_blue">Dark Blue</option>
            <option value="light">Light</option>
          </select>
        </FormField>
        <FormField label="Font Size">
          <select style={selectStyle} value={s.board_font_size || "normal"} onChange={e => set("board_font_size", e.target.value)}>
            <option value="small">Small</option>
            <option value="normal">Normal</option>
            <option value="large">Large</option>
            <option value="xlarge">Extra Large</option>
          </select>
        </FormField>
        <FormField label="Counters per Row" hint="How many counter boxes appear in one row">
          <input style={inputStyle} type="number" value={s.board_counters_per_row || 3} onChange={e => set("board_counters_per_row", parseInt(e.target.value))} min={1} max={8} />
        </FormField>
      </Section>

      <Section title="Audio">
        <Toggle label="Chime before announcements"  checked={s.board_chime_enabled}       onChange={v => set("board_chime_enabled", v)} />
        <Toggle label="Announce staff name"         checked={s.board_announce_staff_name} onChange={v => set("board_announce_staff_name", v)} />
        <FormField label="Audio Language">
          <select style={selectStyle} value={s.board_audio_language || "en"} onChange={e => set("board_audio_language", e.target.value)}>
            <option value="en">English</option>
            <option value="tw">Twi</option>
          </select>
        </FormField>
        <FormField label="Audio Volume" hint={`${s.board_audio_volume ?? 80}%`}>
          <input type="range" min={0} max={100} value={s.board_audio_volume ?? 80}
            onChange={e => set("board_audio_volume", parseInt(e.target.value))}
            style={{ width: "100%", accentColor: "#2563EB" }} />
        </FormField>
        <FormField label="Speech Speed" hint={`${(s.board_speech_rate ?? 1.0).toFixed(2)}x`}>
          <input type="range" min={0.5} max={1.5} step={0.05}
            value={s.board_speech_rate ?? 1.0}
            onChange={e => set("board_speech_rate", parseFloat(e.target.value))}
            style={{ width: "100%", accentColor: "#2563EB" }} />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
            {["0.5x","0.75x","1.0x","1.25x","1.5x"].map(l => (
              <span key={l} style={{ fontSize: "10px", color: "#475569" }}>{l}</span>
            ))}
          </div>
        </FormField>
        <FormField label="Voice Gender" hint="Preferred TTS voice gender for announcements">
          <div style={{ display: "flex", gap: "10px" }}>
            {[["default","System Default"],["female","Female"],["male","Male"]].map(([val, label]) => (
              <label key={val} style={{ display:"flex", alignItems:"center", gap:"6px", cursor:"pointer", fontSize:"13px", color: s.board_voice_gender === val ? "#60A5FA" : "#94A3B8" }}>
                <input type="radio" name="branch_voice_gender" value={val}
                  checked={(s.board_voice_gender ?? "default") === val}
                  onChange={() => set("board_voice_gender", val)}
                  style={{ accentColor:"#2563EB" }} />
                {label}
              </label>
            ))}
          </div>
        </FormField>
      </Section>

      <Section title="Announcement Banner">
        <Toggle label="Show announcement banner" checked={s.board_announcement_active} onChange={v => set("board_announcement_active", v)} />
        {s.board_announcement_active && (
          <FormField label="Announcement Text">
            <textarea
              style={{ ...inputStyle, resize: "vertical", minHeight: "70px" }}
              value={s.board_announcement || ""}
              onChange={e => set("board_announcement", e.target.value)}
              placeholder="e.g. The pharmacy will close at 4PM today."
            />
          </FormField>
        )}
      </Section>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={T.section}>
      <div style={T.sectionTitle}>{title}</div>
      <div style={T.sectionBody}>{children}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <div style={T.toggleRow}>
      <span style={{ fontSize: "13px", color: "#94A3B8", flex: 1 }}>{label}</span>
      <button onClick={() => onChange(!checked)}
        style={{ ...T.toggleBtn, background: checked ? "#2563EB" : "rgba(51,65,85,0.6)" }}>
        <div style={{ ...T.toggleDot, transform: checked ? "translateX(18px)" : "translateX(2px)" }} />
      </button>
    </div>
  );
}

const styles = {
  root:    { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "900px" },
  tabs:    { display: "flex", gap: "4px", borderBottom: "1px solid rgba(37,99,235,0.15)", marginBottom: "24px" },
  tab:     { background: "none", border: "none", borderBottom: "2px solid transparent", padding: "10px 16px", fontSize: "13px", fontWeight: "500", color: "#64748B", cursor: "pointer", fontFamily: "inherit", transition: "color 0.15s", marginBottom: "-1px" },
  tabActive: { color: "#60A5FA", borderBottomColor: "#2563EB" },
  panel:   {},
  saveBar: { position: "sticky", bottom: "20px", background: "rgba(15,23,42,0.95)", border: "1px solid rgba(234,179,8,0.3)", borderRadius: "10px", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "24px", backdropFilter: "blur(10px)" },
};

const T = {
  grid:        { display: "flex", flexDirection: "column", gap: "16px" },
  section:     { background: "rgba(15,23,42,0.5)", border: "1px solid rgba(37,99,235,0.12)", borderRadius: "12px", overflow: "hidden" },
  sectionTitle:{ padding: "12px 18px", fontSize: "11px", fontWeight: "700", color: "#2563EB", letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: "1px solid rgba(37,99,235,0.1)", background: "rgba(37,99,235,0.05)" },
  sectionBody: { padding: "16px 18px", display: "flex", flexDirection: "column", gap: "14px" },
  toggleRow:   { display: "flex", alignItems: "center", gap: "12px" },
  toggleBtn:   { width: "38px", height: "22px", borderRadius: "11px", border: "none", cursor: "pointer", position: "relative", flexShrink: 0, transition: "background 0.2s", padding: 0 },
  toggleDot:   { position: "absolute", top: "3px", width: "16px", height: "16px", borderRadius: "50%", background: "#fff", transition: "transform 0.2s" },
};
